import { type TrialInviteInspection } from '#app/utils/trial-invite.server.ts'

/**
 * What the recipient is about to create. A trial link opened while logged
 * out leads to signup (an account); opened while logged in, it leads to a
 * new organization, since the trial always lands on a fresh organization.
 */
export type TrialInviteTarget = 'account' | 'organization'

const TARGET_LABELS: Record<
	TrialInviteTarget,
	{ activation: string; fallback: string }
> = {
	account: {
		activation: 'la création de votre compte',
		fallback: 'créer un compte',
	},
	organization: {
		activation: 'la création de votre organisation',
		fallback: 'créer une organisation',
	},
}

type TrialPlanType = Extract<
	TrialInviteInspection,
	{ status: 'usable' }
>['planType']

const PLAN_LABELS: Record<TrialPlanType, string> = {
	PRO: 'Pro',
	BUSINESS: 'Business',
	ENTERPRISE: 'Enterprise',
}

export function TrialInviteBanner({
	inspection,
	target,
}: {
	inspection: TrialInviteInspection
	target: TrialInviteTarget
}) {
	const labels = TARGET_LABELS[target]
	if (inspection.status === 'usable') {
		const planLabel = PLAN_LABELS[inspection.planType]
		return (
			<div
				className="mb-6 rounded-lg p-3.5 text-sm"
				style={{
					background: 'var(--brand-soft)',
					border: '1px solid rgba(93, 47, 240, 0.18)',
					color: 'var(--ink)',
				}}
			>
				<span
					className="mb-1.5 block uppercase"
					style={{
						fontFamily: 'var(--font-claire-mono)',
						fontSize: 10.5,
						letterSpacing: '0.14em',
						color: 'var(--brand)',
						fontWeight: 600,
					}}
				>
					Invitation valide
				</span>
				Votre essai <strong>{planLabel}</strong> de{' '}
				<strong>
					{inspection.trialDays} jour
					{inspection.trialDays > 1 ? 's' : ''}
				</strong>{' '}
				sera activé après {labels.activation}.
			</div>
		)
	}
	const reason =
		inspection.status === 'not_found'
			? "Ce lien d'invitation n'est pas valide."
			: inspection.status === 'revoked'
				? "Ce lien d'invitation a été révoqué."
				: inspection.status === 'expired'
					? "Ce lien d'invitation a expiré."
					: inspection.status === 'already_redeemed'
						? 'Vous avez déjà utilisé cette invitation pour une autre organisation : elle donne droit à un seul essai par personne.'
						: 'Ce lien d’invitation a déjà été utilisé.'
	return (
		<div
			className="mb-6 rounded-lg p-3.5 text-sm"
			style={{
				background: 'rgba(216, 138, 29, 0.08)',
				border: '1px solid rgba(216, 138, 29, 0.25)',
				color: '#7A4F12',
			}}
		>
			{reason} Vous pouvez tout de même {labels.fallback} ci-dessous.
		</div>
	)
}
